import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ChevronLeft, ChevronRight, Check, Calendar, Clock, 
  CreditCard, User, Star, Video, MapPin, Shield, 
  AlertCircle, Loader2, CheckCircle
} from 'lucide-react';
import { TutorTrustBadges } from '../../components/services/EducationBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicator
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center mb-8" data-testid="step-indicator">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center">
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                index < currentStep 
                  ? 'bg-green-500 text-white' 
                  : index === currentStep 
                    ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' 
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
            </div>
            <span className={`text-xs mt-1 ${index <= currentStep ? 'text-gray-700' : 'text-gray-400'}`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-16 h-1 mx-2 ${index < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Step 1: Trial Selection
function TrialSelectionStep({ tutor, onSelect, onSkip }) {
  const [selectedOption, setSelectedOption] = useState(null);
  
  const options = [
    {
      id: 'trial',
      title: 'Book a Trial',
      description: '30-minute introductory session at 50% off',
      price: tutor?.hourly_rate ? tutor.hourly_rate * 0.5 * 0.5 : 15,
      features: ['Meet your tutor', 'Discuss learning goals', 'Try teaching style'],
      recommended: true,
    },
    {
      id: 'full',
      title: 'Book Full Session',
      description: 'Standard 1-hour tutoring session',
      price: tutor?.hourly_rate || 30,
      features: ['Full lesson content', 'Homework support', 'Progress tracking'],
    },
    {
      id: 'package',
      title: 'Book 5-Session Package',
      description: 'Save 10% with a package deal',
      price: (tutor?.hourly_rate || 30) * 5 * 0.9,
      features: ['5 full sessions', '10% discount', 'Priority scheduling'],
    },
  ];

  return (
    <div data-testid="trial-selection-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Choose Your Booking Type</h2>
      <p className="text-gray-500 mb-6">Start with a trial to ensure it&apos;s the right fit</p>
      
      <div className="grid gap-4">
        {options.map((option) => (
          <Card 
            key={option.id}
            className={`cursor-pointer transition-all ${
              selectedOption === option.id 
                ? 'border-indigo-500 ring-2 ring-indigo-100' 
                : 'hover:border-gray-300'
            }`}
            onClick={() => setSelectedOption(option.id)}
            data-testid={`option-${option.id}`}
          >
            <CardContent className="p-4 flex items-start gap-4">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedOption === option.id 
                  ? 'border-indigo-600 bg-indigo-600' 
                  : 'border-gray-300'
              }`}>
                {selectedOption === option.id && <Check className="w-4 h-4 text-white" />}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">{option.title}</h3>
                  {option.recommended && (
                    <Badge className="bg-green-100 text-green-700 text-xs">Recommended</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-2">{option.description}</p>
                <ul className="space-y-1">
                  {option.features.map((feature, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="text-right">
                <p className="font-bold text-lg text-indigo-600">
                  {tutor?.currency === 'LKR' || (tutor?.country || '').toUpperCase() === 'LK' || (tutor?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{option.price.toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">
                  {option.id === 'package' ? 'total' : 'per session'}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="flex justify-end mt-6">
        <Button 
          onClick={() => onSelect(options.find(o => o.id === selectedOption))}
          disabled={!selectedOption}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          Continue to Schedule <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// Step 2: Schedule Selection
function ScheduleSelectionStep({ tutor, bookingOption, onSelect, onBack }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Generate next 14 days
  const dates = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return {
      value: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
      dayOfWeek: date.toLocaleDateString('en-GB', { weekday: 'long' }).toLowerCase(),
    };
  });
  
  // Time slots
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00'
  ];
  
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedDate || !tutor?.id) return;
      
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/education-tuition/tutors/${tutor.id}/availability?date=${selectedDate}`
        );
        const data = await res.json();
        if (data.success) {
          setAvailability(data);
        }
      } catch (error) {
        console.error('Failed to fetch availability:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailability();
  }, [selectedDate, tutor?.id]);
  
  const isSlotBooked = (time) => {
    if (!availability?.booked_slots) return false;
    return availability.booked_slots.some(slot => slot.time === time);
  };

  return (
    <div data-testid="schedule-selection-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Choose Date & Time</h2>
      <p className="text-gray-500 mb-6">Select your preferred session time</p>
      
      {/* Date Selection */}
      <div className="mb-6">
        <Label className="mb-2 block">Select Date</Label>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {dates.map((date) => (
            <button
              key={date.value}
              onClick={() => setSelectedDate(date.value)}
              className={`flex-shrink-0 px-4 py-3 rounded-lg border text-center transition-all ${
                selectedDate === date.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`date-${date.value}`}
            >
              <p className="text-xs text-gray-500">{date.label.split(' ')[0]}</p>
              <p className="font-semibold">{date.label.split(' ')[1]}</p>
              <p className="text-xs">{date.label.split(' ')[2]}</p>
            </button>
          ))}
        </div>
      </div>
      
      {/* Time Selection */}
      {selectedDate && (
        <div className="mb-6">
          <Label className="mb-2 block">Select Time</Label>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {timeSlots.map((time) => {
                const isBooked = isSlotBooked(time);
                return (
                  <button
                    key={time}
                    onClick={() => !isBooked && setSelectedTime(time)}
                    disabled={isBooked}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                      isBooked
                        ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                        : selectedTime === time
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`time-${time}`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Delivery Format */}
      {tutor?.availability && (
        <div className="mb-6">
          <Label className="mb-2 block">Session Format</Label>
          <div className="flex gap-3">
            {tutor.availability.online && (
              <Button variant="outline" className="flex items-center gap-2">
                <Video className="w-4 h-4" /> Online
              </Button>
            )}
            {tutor.availability.in_person && (
              <Button variant="outline" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> In-Person
              </Button>
            )}
          </div>
        </div>
      )}
      
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button 
          onClick={() => onSelect({ date: selectedDate, time: selectedTime })}
          disabled={!selectedDate || !selectedTime}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          Continue to Payment <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// Step 3: Payment
function PaymentStep({ tutor, bookingOption, schedule, onComplete, onBack }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    goals: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const currencySymbol = tutor?.currency === 'LKR' || (tutor?.country || '').toUpperCase() === 'LK' || (tutor?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£';
  
  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      setError('Please fill in your name and email');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const bookingPayload = {
        tutor_id: tutor.id,
        student_name: formData.name,
        student_email: formData.email,
        student_phone: formData.phone,
        service_type: 'one_to_one_online',
        subject: tutor.subjects?.[0] || 'General',
        level: tutor.levels?.[0] || 'All Levels',
        delivery_format: 'online',
        is_trial: bookingOption?.id === 'trial',
        session_date: schedule.date,
        session_time: schedule.time,
        duration_minutes: bookingOption?.id === 'trial' ? 30 : 60,
        notes: formData.notes,
        goals: formData.goals,
      };
      
      const res = await fetch(`${API_URL}/api/education-tuition/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingPayload),
      });
      
      const data = await res.json();
      
      if (data.success) {
        // Process payment
        const payRes = await fetch(`${API_URL}/api/education-tuition/bookings/${data.booking_id}/pay`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_method: 'stripe' }),
        });
        
        const payData = await payRes.json();
        
        if (payData.success) {
          onComplete({
            bookingId: data.booking_id,
            bookingNumber: data.booking_number,
            amount: data.total_price,
          });
        } else {
          setError('Payment processing failed. Please try again.');
        }
      } else {
        setError(data.detail || 'Failed to create booking');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="payment-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Complete Your Booking</h2>
      <p className="text-gray-500 mb-6">Enter your details and confirm payment</p>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <div>
            <Label>Full Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              data-testid="input-name"
            />
          </div>
          
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              data-testid="input-email"
            />
          </div>
          
          <div>
            <Label>Phone (Optional)</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+44 7xxx xxx xxx"
              data-testid="input-phone"
            />
          </div>
          
          <div>
            <Label>Learning Goals (Optional)</Label>
            <Textarea
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              placeholder="What do you want to achieve?"
              rows={2}
            />
          </div>
          
          <div>
            <Label>Notes for Tutor (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional information..."
              rows={2}
            />
          </div>
        </div>
        
        {/* Summary */}
        <Card className="bg-gray-50">
          <CardHeader>
            <CardTitle className="text-lg">Booking Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src={tutor?.profile_image} 
                alt={tutor?.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="font-medium">{tutor?.name}</p>
                <p className="text-sm text-gray-500">{tutor?.subjects?.join(', ')}</p>
              </div>
            </div>
            
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Session Type</span>
                <span className="font-medium">{bookingOption?.title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">
                  {new Date(schedule.date).toLocaleDateString('en-GB', { 
                    weekday: 'short', day: 'numeric', month: 'short' 
                  })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Time</span>
                <span className="font-medium">{schedule.time}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Duration</span>
                <span className="font-medium">
                  {bookingOption?.id === 'trial' ? '30 minutes' : '60 minutes'}
                </span>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-xl text-indigo-600">
                  {currencySymbol}{bookingOption?.price?.toFixed(2)}
                </span>
              </div>
            </div>
            
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex-col gap-3">
            <Button 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="confirm-booking-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay {currencySymbol}{bookingOption?.price?.toFixed(2)}
                </>
              )}
            </Button>
            <p className="text-xs text-center text-gray-500">
              <Shield className="w-3 h-3 inline mr-1" />
              Secure payment powered by Stripe
            </p>
          </CardFooter>
        </Card>
      </div>
      
      <div className="flex justify-start mt-6">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>
    </div>
  );
}

// Success Screen
function BookingSuccess({ bookingDetails, tutor }) {
  const navigate = useNavigate();
  const currencySymbol = tutor?.currency === 'LKR' || (tutor?.country || '').toUpperCase() === 'LK' || (tutor?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£';
  
  return (
    <div className="text-center py-8" data-testid="booking-success">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
      <p className="text-gray-500 mb-6">
        Your session with {tutor?.name} has been booked successfully.
      </p>
      
      <Card className="max-w-md mx-auto mb-6">
        <CardContent className="p-6 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-500">Booking Number</span>
            <span className="font-mono font-bold">{bookingDetails.bookingNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Amount Paid</span>
            <span className="font-semibold text-green-600">
              {currencySymbol}{bookingDetails.amount?.toFixed(2)}
            </span>
          </div>
        </CardContent>
      </Card>
      
      <p className="text-sm text-gray-500 mb-6">
        A confirmation email has been sent to your email address.
        <br />
        The tutor will confirm and send you the meeting details.
      </p>
      
      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </Button>
        <Button 
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => navigate('/category/education-tuition')}
        >
          Browse More Tutors
        </Button>
      </div>
    </div>
  );
}

// Main Component
export default function EducationBookingFlow() {
  const { tutorId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Booking state
  const [bookingOption, setBookingOption] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  const isTrial = searchParams.get('trial') === 'true';
  
  const steps = [
    { id: 'trial', label: 'Session Type' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'payment', label: 'Payment' },
  ];
  
  useEffect(() => {
    const fetchTutor = async () => {
      try {
        const res = await fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}`);
        const data = await res.json();
        
        if (data.success) {
          setTutor(data.tutor);
        } else {
          setError('Tutor not found');
        }
      } catch (err) {
        setError('Failed to load tutor details');
      } finally {
        setLoading(false);
      }
    };
    
    if (tutorId) {
      fetchTutor();
    }
  }, [tutorId]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-500 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="education-booking-flow">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Tutor Profile
          </Button>
          
          {/* Tutor Mini Card */}
          {tutor && !bookingDetails && (
            <Card className="mb-6">
              <CardContent className="p-4 flex items-center gap-4">
                <img 
                  src={tutor.profile_image} 
                  alt={tutor.name}
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-indigo-100"
                />
                <div className="flex-1">
                  <h2 className="font-semibold text-lg">{tutor.name}</h2>
                  <p className="text-sm text-gray-500">{tutor.subjects?.join(' • ')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-medium">{tutor.rating}</span>
                    <span className="text-gray-400">({tutor.total_reviews} reviews)</span>
                  </div>
                </div>
                <TutorTrustBadges badges={tutor.badges} maxDisplay={3} size="sm" />
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Step Indicator */}
        {!bookingDetails && <StepIndicator currentStep={currentStep} steps={steps} />}
        
        {/* Step Content */}
        <Card>
          <CardContent className="p-6">
            {bookingDetails ? (
              <BookingSuccess bookingDetails={bookingDetails} tutor={tutor} />
            ) : currentStep === 0 ? (
              <TrialSelectionStep 
                tutor={tutor}
                onSelect={(option) => {
                  setBookingOption(option);
                  setCurrentStep(1);
                }}
              />
            ) : currentStep === 1 ? (
              <ScheduleSelectionStep
                tutor={tutor}
                bookingOption={bookingOption}
                onSelect={(scheduleData) => {
                  setSchedule(scheduleData);
                  setCurrentStep(2);
                }}
                onBack={() => setCurrentStep(0)}
              />
            ) : (
              <PaymentStep
                tutor={tutor}
                bookingOption={bookingOption}
                schedule={schedule}
                onComplete={(details) => setBookingDetails(details)}
                onBack={() => setCurrentStep(1)}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
